class PrimeflowProductDrawer {
  constructor(root) {
    this.root = root;
    this.drawerId = root.dataset.drawerId;
    this.currencyCode = root.dataset.currencyCode || 'EUR';
    this.routesRoot = this.normalizeRoot(root.dataset.routesRoot || window.Shopify?.routes?.root || '/');
    this.overlay = root.querySelector('[data-drawer-overlay]');
    this.panel = root.querySelector('[data-drawer-panel]');
    this.closeButton = root.querySelector('[data-drawer-close]');
    this.mainImage = root.querySelector('[data-drawer-main-image]');
    this.mainMedia = root.querySelector('[data-drawer-main-media]');
    this.submitButton = root.querySelector('[data-drawer-submit]');
    this.priceRow = root.querySelector('[data-drawer-price-row]');
    this.priceTarget = root.querySelector('[data-drawer-price]');
    this.bundleLabelTarget = root.querySelector('[data-drawer-bundle-label]');
    this.statusTarget = root.querySelector('[data-drawer-status]');
    this.includedItems = root.querySelector('[data-drawer-included-items]');
    this.includedNameTarget = root.querySelector('[data-drawer-included-name]');
    this.includedPriceTarget = root.querySelector('[data-drawer-included-price]');
    this.thumbTrack = root.querySelector('[data-drawer-thumbs-track]');
    this.bundleOptions = Array.from(root.querySelectorAll('[data-drawer-bundle-option]'));
    this.thumbButtons = Array.from(root.querySelectorAll('[data-drawer-thumb]'));
    this.tabButtons = Array.from(root.querySelectorAll('[data-drawer-tab]'));
    this.tabPanels = Array.from(root.querySelectorAll('[data-drawer-tab-panel]'));
    this.upsellCard = root.querySelector('[data-drawer-upsell-card]');
    this.upsellToggleButton = root.querySelector('[data-drawer-upsell-toggle]');
    this.upsellBuyNowButton = root.querySelector('[data-drawer-upsell-buy-now]');
    this.upsellDefaultLabel = this.upsellToggleButton ? this.upsellToggleButton.textContent.trim() : '';
    this.content = root.querySelector('.primeflow-product-drawer__content');
    this.selectedVariantId = '';
    this.selectedVariantAvailable = false;
    this.selectedUpsell = false;
    this.isSubmitting = false;
    this.lastTrigger = null;
    this.preloadedImages = new Map();
    this.selectedBasePriceText = this.priceTarget ? this.priceTarget.textContent.trim() : '';
    this.selectedBasePriceCents = this.getInitialPriceCents();
    this.defaultBundleLabel = this.bundleLabelTarget ? this.bundleLabelTarget.textContent.trim() : '';
    this.defaultBasePriceText = this.selectedBasePriceText;
    this.defaultBasePriceCents = this.selectedBasePriceCents;
    this.defaultMainImage = this.mainImage
      ? {
          src: this.mainImage.getAttribute('src') || '',
          alt: this.mainImage.getAttribute('alt') || '',
          fit: this.mainImage.style.objectFit || 'cover',
          background: this.mainMedia?.style.background || '',
        }
      : null;
    this.thumbAutoplayId = null;
    this.mainImageAnimationId = null;
    this.thumbAutoplayDelay = 4200;
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundRootClick = this.handleRootClick.bind(this);
    this.root.addEventListener('click', this.boundRootClick);
    document.addEventListener('click', this.boundDocumentClick);
    document.addEventListener('keydown', this.boundKeydown);
    this.initialize();
  }

  initialize() {
    this.preloadGalleryImages();

    this.selectedUpsell = false;
    this.updateUpsellState(false);

    const initialBundle = this.bundleOptions.find((option) => option.classList.contains('is-active'));
    if (initialBundle) {
      this.selectBundle(initialBundle, false);
    } else {
      this.resetBundleSelection({ restoreMainImage: true });
    }
  }

  normalizeRoot(path) {
    return path.endsWith('/') ? path : `${path}/`;
  }

  getInitialPriceCents() {
    const priceCents = Number.parseInt(this.priceTarget?.dataset.priceCents || '', 10);
    if (Number.isFinite(priceCents) && priceCents >= 0) return priceCents;
    return this.parsePriceToCents(this.selectedBasePriceText);
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest('[data-primeflow-drawer-trigger]');
    if (!trigger || trigger.dataset.primeflowDrawerTrigger !== this.drawerId) return;
    event.preventDefault();
    this.lastTrigger = trigger;

    this.clearStatus();

    const variantId = trigger.dataset.primeflowVariantId || '';
    const productLabel = trigger.dataset.primeflowProductLabel || '';
    const productPrice = trigger.dataset.primeflowProductPrice || '';
    const shouldAddDigital = trigger.dataset.primeflowDrawerAddDigital === 'true';
    let matchedBundle = false;

    if (variantId) {
      const bundleByVariant = this.bundleOptions.find((option) => option.dataset.variantId === variantId);
      if (bundleByVariant) {
        this.selectBundle(bundleByVariant, false);
        matchedBundle = true;
      } else {
        this.applyVariantSelection(variantId, true);
        if (this.bundleLabelTarget && productLabel) this.bundleLabelTarget.textContent = productLabel;
        if (productPrice) {
          this.selectedBasePriceText = productPrice;
          this.selectedBasePriceCents = this.parsePriceToCents(productPrice);
          this.updateDisplayedPrice();
        }
      }
    }

    const bundleIndex = Number.parseInt(trigger.dataset.primeflowBundleIndex || '', 10);
    if (!matchedBundle && Number.isInteger(bundleIndex) && this.bundleOptions[bundleIndex]) {
      this.selectBundle(this.bundleOptions[bundleIndex], false);
    }

    if (trigger.dataset.primeflowDrawerFocus === 'digital-edition') {
      this.resetBundleSelection({ restoreMainImage: true, resetUpsell: !shouldAddDigital });
      if (shouldAddDigital) {
        this.toggleUpsell(true);
      }
    }

    this.open(trigger);
  }

  handleKeydown(event) {
    if (!this.root.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.maintainFocus(event);
    }
  }

  handleRootClick(event) {
    const closeTrigger = event.target.closest('[data-drawer-close]');
    if (closeTrigger || event.target === this.overlay) {
      event.preventDefault();
      this.close();
      return;
    }

    const thumb = event.target.closest('[data-drawer-thumb]');
    if (thumb) {
      event.preventDefault();
      this.selectThumb(thumb);
      return;
    }

    const prevMedia = event.target.closest('[data-drawer-prev-media]');
    if (prevMedia) {
      event.preventDefault();
      if (this.shouldPageThumbs()) {
        this.pageThumbs(-1);
      } else {
        this.stepThumbSelection(-1);
      }
      return;
    }

    const nextMedia = event.target.closest('[data-drawer-next-media]');
    if (nextMedia) {
      event.preventDefault();
      if (this.shouldPageThumbs()) {
        this.pageThumbs(1);
      } else {
        this.stepThumbSelection(1);
      }
      return;
    }

    const tab = event.target.closest('[data-drawer-tab]');
    if (tab) {
      event.preventDefault();
      this.selectTab(tab.dataset.drawerTab);
      return;
    }

    const bundle = event.target.closest('[data-drawer-bundle-option]');
    if (bundle) {
      event.preventDefault();
      if (bundle.classList.contains('is-active')) {
        this.resetBundleSelection({ restoreMainImage: true });
        bundle.focus({ preventScroll: true });
        return;
      }
      this.selectBundle(bundle, true);
      return;
    }

    const removeUpsell = event.target.closest('[data-drawer-remove-upsell]');
    if (removeUpsell) {
      event.preventDefault();
      this.toggleUpsell(false);
      return;
    }

    const upsellToggle = event.target.closest('[data-drawer-upsell-toggle]');
    if (upsellToggle) {
      event.preventDefault();
      this.toggleUpsell();
      return;
    }

    const upsellBuyNow = event.target.closest('[data-drawer-upsell-buy-now]');
    if (upsellBuyNow) {
      event.preventDefault();
      this.startDigitalCheckout(upsellBuyNow);
      return;
    }

    const submit = event.target.closest('[data-drawer-submit]');
    if (submit) {
      event.preventDefault();
      if (!this.selectedVariantId || !this.selectedVariantAvailable) {
        if (this.selectedUpsell) {
          this.startDigitalCheckout();
        }
        return;
      }
      this.startCheckout();
    }
  }

  open(trigger = null) {
    if (trigger) this.lastTrigger = trigger;
    this.root.classList.add('is-open');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.startThumbAutoplay();
    window.setTimeout(() => {
      if (trigger?.dataset.primeflowDrawerFocus === 'digital-edition') {
        this.focusDigitalEdition();
        return;
      }
      this.focusInitialElement();
    }, 120);
  }

  close() {
    this.root.classList.remove('is-open');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.stopThumbAutoplay();
    this.clearStatus();
    if (this.lastTrigger instanceof HTMLElement) {
      this.lastTrigger.focus({ preventScroll: true });
    }
  }

  getFocusableElements() {
    if (!this.panel) return [];

    return Array.from(
      this.panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.getClientRects().length > 0);
  }

  focusInitialElement() {
    const [firstFocusable] = this.getFocusableElements();
    if (firstFocusable instanceof HTMLElement) {
      firstFocusable.focus({ preventScroll: true });
      return;
    }

    if (this.panel instanceof HTMLElement) {
      this.panel.focus({ preventScroll: true });
    }
  }

  focusDigitalEdition() {
    if (this.upsellCard instanceof HTMLElement) {
      this.upsellCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    const focusTarget = this.upsellBuyNowButton || this.upsellToggleButton || this.upsellCard;
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
      return;
    }

    this.focusInitialElement();
  }

  resetBundleSelection({ restoreMainImage = false, resetUpsell = false } = {}) {
    this.bundleOptions.forEach((option) => option.classList.remove('is-active'));

    this.selectedBasePriceText = this.defaultBasePriceText;
    this.selectedBasePriceCents = this.defaultBasePriceCents;

    if (this.bundleLabelTarget) {
      this.bundleLabelTarget.textContent = this.defaultBundleLabel;
    }

    if (resetUpsell) {
      this.selectedUpsell = false;
      this.updateUpsellState(false);
    }

    this.applyVariantSelection('', false);

    if (restoreMainImage) {
      this.restoreDefaultMainImage();
    }

    this.updateDisplayedPrice();
  }

  restoreDefaultMainImage() {
    if (this.defaultMainImage?.src) {
      this.setMainImage(this.defaultMainImage);
      return;
    }

    if (this.mainMedia) {
      this.mainMedia.style.background = this.defaultMainImage?.background || '';
    }
  }

  maintainFocus(event) {
    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey) {
      if (activeElement === firstFocusable || activeElement === this.panel) {
        event.preventDefault();
        lastFocusable.focus({ preventScroll: true });
      }
      return;
    }

    if (activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus({ preventScroll: true });
    }
  }

  setMainImage({ src, alt = '', fit = 'cover', background = '' }) {
    if (!this.mainImage || !src) return;

    this.preloadImage(src);

    if (this.mainImage.getAttribute('src') !== src) {
      this.mainImage.src = src;
    }

    this.mainImage.alt = alt;
    this.mainImage.style.objectFit = fit;
    this.mainImage.style.opacity = '1';
    if (this.mainMedia) this.mainMedia.style.background = background || '';
    this.restartMainImageAnimation();
  }

  preloadGalleryImages() {
    const imageSources = new Set();

    this.thumbButtons.forEach((button) => {
      if (button.dataset.imageSrc) {
        imageSources.add(button.dataset.imageSrc);
      }
    });

    this.bundleOptions.forEach((option) => {
      if (option.dataset.bundleImageSrc) {
        imageSources.add(option.dataset.bundleImageSrc);
      }
    });

    imageSources.forEach((src) => {
      this.preloadImage(src);
    });
  }

  preloadImage(src) {
    if (!src) return Promise.resolve(null);

    const existingPreload = this.preloadedImages.get(src);
    if (existingPreload) return existingPreload;

    const preloadPromise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = src;

      if (image.complete) {
        resolve(image);
      }
    });

    this.preloadedImages.set(src, preloadPromise);
    return preloadPromise;
  }

  selectThumb(thumb) {
    this.thumbButtons.forEach((button) => button.classList.toggle('is-active', button === thumb));
    this.scrollThumbIntoView(thumb);
    this.setMainImage({
      src: thumb.dataset.imageSrc,
      alt: thumb.dataset.imageAlt || '',
      fit: thumb.dataset.imageFit || 'cover',
      background: thumb.dataset.imageBg || '',
    });
  }

  stepThumbSelection(direction) {
    if (!this.thumbButtons.length) return;

    const activeIndex = this.thumbButtons.findIndex((button) => button.classList.contains('is-active'));
    const currentIndex = activeIndex >= 0 ? activeIndex : 0;
    const nextIndex = (currentIndex + direction + this.thumbButtons.length) % this.thumbButtons.length;
    this.selectThumb(this.thumbButtons[nextIndex]);
  }

  shouldPageThumbs() {
    return Boolean(this.thumbTrack) && window.matchMedia('(max-width: 989px)').matches;
  }

  shouldAutoplayThumbs() {
    return this.shouldPageThumbs() && this.thumbButtons.length > 1;
  }

  pageThumbs(direction) {
    if (!this.thumbTrack) return;

    this.thumbTrack.scrollBy({
      left: direction * this.thumbTrack.clientWidth,
      behavior: 'smooth',
    });
  }

  startThumbAutoplay() {
    this.stopThumbAutoplay();
    if (!this.shouldAutoplayThumbs()) return;

    this.thumbAutoplayId = window.setInterval(() => {
      this.stepThumbSelection(1);
    }, this.thumbAutoplayDelay);
  }

  stopThumbAutoplay() {
    if (!this.thumbAutoplayId) return;

    window.clearInterval(this.thumbAutoplayId);
    this.thumbAutoplayId = null;
  }

  scrollThumbIntoView(thumb) {
    if (!thumb || !this.shouldPageThumbs() || !this.thumbTrack || this.thumbTrack.offsetParent === null) return;
    thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  restartMainImageAnimation() {
    if (!this.mainMedia) return;

    this.mainMedia.classList.remove('is-transitioning');
    void this.mainMedia.offsetWidth;
    this.mainMedia.classList.add('is-transitioning');

    if (this.mainImageAnimationId) {
      window.clearTimeout(this.mainImageAnimationId);
    }

    this.mainImageAnimationId = window.setTimeout(() => {
      if (!this.mainMedia) return;
      this.mainMedia.classList.remove('is-transitioning');
      this.mainImageAnimationId = null;
    }, 900);
  }

  selectTab(tabId) {
    this.tabButtons.forEach((button) => button.classList.toggle('is-active', button.dataset.drawerTab === tabId));
    this.tabPanels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.drawerTabPanel === tabId));
  }

  selectBundle(bundle, focusButton) {
    this.bundleOptions.forEach((option) => option.classList.toggle('is-active', option === bundle));

    this.selectedBasePriceText = bundle.dataset.bundlePrice || this.selectedBasePriceText;
    const bundlePriceCents = Number.parseInt(bundle.dataset.bundlePriceCents || '', 10);
    this.selectedBasePriceCents = Number.isFinite(bundlePriceCents)
      ? bundlePriceCents
      : this.parsePriceToCents(this.selectedBasePriceText);

    if (this.bundleLabelTarget) {
      this.bundleLabelTarget.textContent = bundle.dataset.bundleLabel || '';
    }

    const variantId = bundle.dataset.variantId || '';
    const available = bundle.dataset.available !== 'false';
    this.applyVariantSelection(variantId, available);
    this.updateDisplayedPrice();

    if (bundle.dataset.bundleImageSrc) {
      this.setMainImage({
        src: bundle.dataset.bundleImageSrc,
        alt: bundle.dataset.bundleImageAlt || bundle.dataset.bundleLabel || '',
        fit: 'cover',
        background: '',
      });
    }

    if (focusButton && this.submitButton) {
      this.submitButton.focus({ preventScroll: true });
    }
  }

  applyVariantSelection(variantId, available) {
    this.selectedVariantId = variantId;
    this.selectedVariantAvailable = available;

    if (!this.submitButton) return;

    if (!variantId || !available || this.isSubmitting) {
      this.submitButton.setAttribute('disabled', 'disabled');
    } else {
      this.submitButton.removeAttribute('disabled');
    }
  }

  toggleUpsell(forceState) {
    if (!this.upsellCard) return;
    const variantId = this.upsellCard.dataset.upsellVariantId || '';
    const available = this.upsellCard.dataset.upsellAvailable !== 'false';
    if (!variantId || !available) return;

    const nextState = typeof forceState === 'boolean' ? forceState : !this.selectedUpsell;
    this.selectedUpsell = nextState;
    this.updateUpsellState(nextState);
    this.updateDisplayedPrice();
  }

  updateUpsellState(selected) {
    if (this.upsellCard) {
      this.upsellCard.classList.toggle('is-active', selected);
    }

    if (this.upsellToggleButton) {
      this.upsellToggleButton.textContent = selected ? 'In Cart' : this.upsellDefaultLabel;
    }

    if (this.includedItems) {
      this.includedItems.hidden = !selected;
    }

    if (selected) {
      if (this.includedNameTarget && this.upsellCard?.dataset.upsellName) {
        this.includedNameTarget.textContent = this.upsellCard.dataset.upsellName;
      }
      if (this.includedPriceTarget && this.upsellCard?.dataset.upsellPrice) {
        this.includedPriceTarget.textContent = this.upsellCard.dataset.upsellPrice;
      }
    }
  }

  updateDisplayedPrice() {
    if (!this.priceTarget) return;

    const totalCents = this.selectedBasePriceCents + (this.selectedUpsell ? this.getUpsellPriceCents() : 0);
    if (totalCents > 0) {
      if (this.priceRow) {
        this.priceRow.hidden = false;
      }
      this.priceTarget.textContent = this.formatMoney(totalCents);
      return;
    }

    const fallbackPriceText = this.selectedBasePriceText || '';
    this.priceTarget.textContent = fallbackPriceText;

    if (this.priceRow) {
      this.priceRow.hidden = fallbackPriceText === '';
    }
  }

  getUpsellPriceCents() {
    const upsellPriceCents = Number.parseInt(this.upsellCard?.dataset.upsellPriceCents || '', 10);
    if (Number.isFinite(upsellPriceCents) && upsellPriceCents >= 0) return upsellPriceCents;
    return this.parsePriceToCents(this.upsellCard?.dataset.upsellPrice || '');
  }

  formatMoney(cents) {
    if (!Number.isFinite(cents) || cents < 0) {
      console.warn('[PrimeFlow Drawer] Invalid cents value for formatMoney', cents);
      return '';
    }
    const amount = cents / 100;
    const hasDecimals = Math.round(cents) % 100 !== 0;
    const currency = (this.currencyCode || 'EUR').trim().toUpperCase();
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      }).format(amount);
    } catch (error) {
      console.warn('[PrimeFlow Drawer] formatMoney Intl fallback', { cents, currency, error });
      const symbol = currency === 'EUR' ? '€' : currency;
      return `${symbol}${amount.toFixed(hasDecimals ? 2 : 0)}`;
    }
  }

  parsePriceToCents(priceText) {
    if (!priceText || typeof priceText !== 'string') return 0;
    try {
      const cleaned = priceText.replace(/[^0-9,.-]/g, '').trim();
      if (!cleaned) return 0;

      let normalized = cleaned;

      if (cleaned.includes(',') && cleaned.includes('.')) {
        normalized = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')
          ? cleaned.replace(/\./g, '').replace(',', '.')
          : cleaned.replace(/,/g, '');
      } else if (cleaned.includes(',')) {
        const parts = cleaned.split(',');
        normalized = parts[parts.length - 1].length === 2
          ? `${parts.slice(0, -1).join('')}.${parts[parts.length - 1]}`
          : cleaned.replace(/,/g, '');
      } else if (cleaned.includes('.')) {
        const parts = cleaned.split('.');
        normalized = parts[parts.length - 1].length === 2 ? cleaned : cleaned.replace(/\./g, '');
      }

      const parsed = Number.parseFloat(normalized);
      return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
    } catch (error) {
      console.warn('[PrimeFlow Drawer] parsePriceToCents failed', { priceText, error });
      return 0;
    }
  }

  clearStatus() {
    if (!this.statusTarget) return;
    this.statusTarget.hidden = true;
    this.statusTarget.textContent = '';
  }

  showStatus(message) {
    if (!this.statusTarget) return;
    this.statusTarget.hidden = false;
    this.statusTarget.textContent = message;
  }

  async startDigitalCheckout(button = this.upsellBuyNowButton) {
    if (this.isSubmitting) return;

    const rawVariantId = this.upsellCard?.dataset.upsellVariantId || '';
    const variantId = Number.parseInt(rawVariantId, 10);
    const available = this.upsellCard?.dataset.upsellAvailable !== 'false';
    if (!Number.isInteger(variantId) || variantId <= 0 || !available) {
      console.warn('[PrimeFlow Drawer] Digital checkout blocked', { rawVariantId, variantId, available });
      return;
    }

    this.clearStatus();
    this.isSubmitting = true;
    if (button instanceof HTMLElement) {
      button.setAttribute('disabled', 'disabled');
    }
    this.applyVariantSelection(this.selectedVariantId, this.selectedVariantAvailable);

    try {
      const clearResponse = await fetch(`${this.routesRoot}cart/clear.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!clearResponse.ok) {
        throw new Error('Unable to reset the cart right now.');
      }

      const response = await fetch(`${this.routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const data = await response.json();

      if (!response.ok || data?.status) {
        let message = 'Unable to start checkout right now.';
        if (data?.description) message = data.description;
        throw new Error(message);
      }

      window.location.href = `${this.routesRoot}checkout`;
    } catch (error) {
      console.error('[PrimeFlow Drawer] Digital checkout failed', error);
      this.showStatus(error instanceof Error ? error.message : 'Unable to start checkout right now.');
    } finally {
      this.isSubmitting = false;
      if (button instanceof HTMLElement && available) {
        button.removeAttribute('disabled');
      }
      this.applyVariantSelection(this.selectedVariantId, this.selectedVariantAvailable);
    }
  }

  async startCheckout() {
    if (this.isSubmitting) return;

    const rawVariantId = this.selectedVariantId || '';
    const selectedId = Number.parseInt(rawVariantId, 10);
    if (!Number.isInteger(selectedId) || selectedId <= 0 || !this.selectedVariantAvailable) {
      console.warn('[PrimeFlow Drawer] Physical checkout blocked', { rawVariantId, selectedId, available: this.selectedVariantAvailable });
      return;
    }

    const items = [{ id: selectedId, quantity: 1 }];
    const upsellVariantId = Number.parseInt(this.upsellCard?.dataset.upsellVariantId || '', 10);
    if (this.selectedUpsell && Number.isInteger(upsellVariantId) && upsellVariantId > 0) {
      items.push({ id: upsellVariantId, quantity: 1 });
    }

    this.clearStatus();
    this.isSubmitting = true;
    if (this.submitButton) {
      this.submitButton.classList.add('is-loading');
    }
    this.applyVariantSelection(this.selectedVariantId, this.selectedVariantAvailable);

    try {
      const clearResponse = await fetch(`${this.routesRoot}cart/clear.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!clearResponse.ok) {
        throw new Error('Unable to reset the cart right now.');
      }

      const response = await fetch(`${this.routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok || data?.status) {
        let message = 'Unable to start checkout right now.';
        if (data?.description) message = data.description;
        throw new Error(message);
      }

      window.location.href = `${this.routesRoot}checkout`;
    } catch (error) {
      console.error('[PrimeFlow Drawer] Physical checkout failed', error);
      this.showStatus(error instanceof Error ? error.message : 'Unable to start checkout right now.');
    } finally {
      this.isSubmitting = false;
      if (this.submitButton) {
        this.submitButton.classList.remove('is-loading');
      }
      this.applyVariantSelection(this.selectedVariantId, this.selectedVariantAvailable);
    }
  }
}

function initializePrimeflowProductDrawers(root = document) {
  root.querySelectorAll('[data-primeflow-drawer-root]').forEach((drawerRoot) => {
    if (drawerRoot.dataset.primeflowDrawerInitialized === 'true') return;
    drawerRoot.dataset.primeflowDrawerInitialized = 'true';
    new PrimeflowProductDrawer(drawerRoot);
  });
}

initializePrimeflowProductDrawers();

document.addEventListener('shopify:section:load', (event) => {
  initializePrimeflowProductDrawers(event.target);
});